import { useMemo, useState, useEffect, useRef } from "react";
import * as LucideIcons from "lucide-react";
import { Search, X, ChevronDown } from "lucide-react";

// Curated subset of Lucide icons (covers most use cases without overwhelming the picker).
// Add more keys here when needed.
const ICON_LIBRARY = [
  // General / UI
  "Sparkles", "Star", "Heart", "Flame", "Zap", "Award", "Trophy", "Crown",
  "Bookmark", "Tag", "Flag", "Bell", "Eye", "Check", "CheckCircle", "CheckCircle2",
  "X", "Plus", "Minus", "Info", "AlertCircle", "AlertTriangle", "HelpCircle", "Smile",
  // People / Customer
  "User", "Users", "UserPlus", "UserCheck", "UserCircle", "Contact", "PersonStanding",
  "Smile", "Crown", "Baby",
  // Commerce / Money
  "Euro", "DollarSign", "CreditCard", "Wallet", "PiggyBank", "Receipt", "ShoppingBag",
  "ShoppingCart", "Gift", "Percent", "Tag", "Tags", "BadgePercent",
  // Tools / Industry
  "Scissors", "Brush", "PaintBucket", "Palette", "Sparkles", "Car", "Stethoscope",
  "Wrench", "Hammer", "Screwdriver", "Drill", "Cog", "Settings", "Briefcase",
  "PawPrint", "Camera", "Film", "Music", "Mic", "Headphones", "Gamepad2", "Dumbbell",
  // Layout / Web
  "Layout", "LayoutDashboard", "LayoutGrid", "LayoutList", "Layers", "PanelTop",
  "PanelLeft", "PanelRight", "PanelBottom", "Grid3x3", "Columns", "Rows",
  // Communication
  "Mail", "MessageSquare", "MessageCircle", "Send", "Phone", "PhoneCall",
  "Smartphone", "Mic", "Video",
  // Calendar / Time
  "Calendar", "CalendarDays", "CalendarClock", "CalendarCheck", "CalendarPlus",
  "Clock", "Timer", "Hourglass",
  // Charts / Analytics
  "BarChart", "BarChart3", "LineChart", "PieChart", "TrendingUp", "TrendingDown",
  "Activity", "Gauge", "Target",
  // Media / Files
  "Image", "FileText", "File", "FileImage", "Files", "Folder", "FolderOpen",
  "Paperclip", "Download", "Upload",
  // Map / Travel
  "MapPin", "Map", "Navigation", "Compass", "Globe", "Plane", "Car",
  // Status / Security
  "Lock", "Unlock", "Shield", "ShieldCheck", "Key", "Eye", "EyeOff",
  // Misc useful
  "Search", "Filter", "Settings", "Wrench", "Cog", "Sliders", "ToggleLeft",
  "Sun", "Moon", "Cloud", "Snowflake", "Coffee", "Leaf", "TreePine",
  "Book", "BookOpen", "Library", "GraduationCap", "Lightbulb", "Rocket",
  "ThumbsUp", "Handshake", "PartyPopper",
];

// Deduplicate
const UNIQUE_ICONS = Array.from(new Set(ICON_LIBRARY));

/**
 * IconPicker — popover with searchable Lucide icon grid.
 *
 * Props:
 *   value: string (lucide icon name)
 *   onChange: (newIconName) => void
 *   testId: optional data-testid prefix
 *   placeholder: string shown when no icon selected
 *   size: "sm" | "md"
 */
export default function IconPicker({ value, onChange, testId = "icon-picker", placeholder = "Icon wählen", size = "md" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!open) return;
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return UNIQUE_ICONS;
    return UNIQUE_ICONS.filter((n) => n.toLowerCase().includes(needle));
  }, [q]);

  const Current = (value && LucideIcons[value]) || LucideIcons.Sparkles;
  const isMd = size === "md";

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      <button
        type="button"
        data-testid={`${testId}-trigger`}
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-2 mv-input ${isMd ? "" : "text-sm py-1.5"}`}
        style={{ cursor: "pointer" }}
      >
        <Current className={isMd ? "w-5 h-5" : "w-4 h-4"} strokeWidth={1.5} style={{ flexShrink: 0, color: "var(--mv-primary)" }} />
        <span className={`flex-1 text-left ${value ? "" : "opacity-60"}`}>
          {value || placeholder}
        </span>
        <ChevronDown className="w-4 h-4 opacity-60" strokeWidth={1.5} />
      </button>

      {open && (
        <div
          data-testid={`${testId}-popover`}
          className="absolute z-50 mt-1 w-[320px] max-h-[420px] rounded-lg shadow-xl border overflow-hidden flex flex-col"
          style={{ background: "var(--mv-surface, #fff)", borderColor: "var(--mv-border)" }}
        >
          <div className="p-2.5 border-b flex items-center gap-2" style={{ borderColor: "var(--mv-border)" }}>
            <Search className="w-3.5 h-3.5 opacity-60" strokeWidth={1.5} />
            <input
              autoFocus
              data-testid={`${testId}-search`}
              type="text"
              placeholder="Icon suchen…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm"
            />
            {value && (
              <button
                type="button"
                data-testid={`${testId}-clear`}
                onClick={() => { onChange(""); setOpen(false); }}
                className="text-xs opacity-60 hover:opacity-100"
                title="Auswahl entfernen"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            )}
          </div>

          <div className="overflow-y-auto p-2 grid grid-cols-6 gap-1">
            {filtered.length === 0 ? (
              <p className="col-span-6 text-xs p-3 opacity-60">Kein Treffer für &quot;{q}&quot;.</p>
            ) : (
              filtered.map((name) => {
                const Icon = LucideIcons[name];
                if (!Icon) return null;
                const active = name === value;
                return (
                  <button
                    key={name}
                    type="button"
                    data-testid={`${testId}-opt-${name}`}
                    onClick={() => { onChange(name); setOpen(false); }}
                    title={name}
                    className="aspect-square flex items-center justify-center rounded-md transition-colors hover:bg-[var(--mv-surface)]"
                    style={{
                      background: active ? "var(--mv-primary)" : "transparent",
                      color: active ? "#fff" : "var(--mv-text)",
                    }}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                );
              })
            )}
          </div>

          <div className="px-3 py-2 text-[10px] opacity-60 border-t" style={{ borderColor: "var(--mv-border)" }}>
            {filtered.length} Icons · Lucide
          </div>
        </div>
      )}
    </div>
  );
}
